import { isAxiosError } from 'axios'
import { CampaignError } from '@/domain/campaign-errors'

// Maps an HTTP-backed Campaign port failure to a CampaignError category. This is
// the only place an Axios error shape or HTTP status is read for Campaigns —
// domain/campaign-errors.ts stays transport-neutral. Categorization is by
// originating endpoint (`transitionEndpoint`, `fileEndpoint`), never by a
// response payload field: the shared 422 envelope carries no Campaign-specific
// code or `allowed` array. The two endpoint flags are mutually exclusive by call
// site — the lifecycle adapter is the only caller passing `transitionEndpoint`,
// the download adapter the only one passing `fileEndpoint` — so no precedence
// rule between them is needed.

export function mapCampaignHttpError(
	error: unknown,
	options?: { readonly transitionEndpoint?: boolean; readonly fileEndpoint?: boolean },
): CampaignError {
	const status = isAxiosError(error) ? error.response?.status : undefined

	if (status === 400)
		return new CampaignError('validation', 'The request was invalid. Please review the fields and try again.')
	if (status === 404)
		return options?.fileEndpoint
			? new CampaignError('not-found', 'That File could not be found.')
			: new CampaignError('not-found', 'That Campaign could not be found.')
	if (status === 401 || status === 403)
		return new CampaignError('forbidden', 'You do not have permission to perform this action.')
	if (status === 409)
		return new CampaignError('conflict', 'This Campaign changed since it was loaded. Reload and try again.')
	if (status === 422) {
		return options?.transitionEndpoint
			? new CampaignError('invalid-transition', 'Transitioning to this status is not allowed from the current status.')
			: new CampaignError('validation', 'The request was invalid. Please review the fields and try again.')
	}
	return new CampaignError('failure', 'Something went wrong. Please try again.')
}
